/**
 * components/legal/vop/openPoints.ts
 *
 * An open point: a value counsel's draft leaves to the company, written into
 * the text where it belongs so that it is read in its sentence and cannot be
 * lost. `[ DOPLNIT — … ]` in Czech, `[ TO FILL — … ]` in English, the marker
 * `companyDetails.ts` already uses for a fact nobody has decided.
 *
 * It is a marker and not a guess on purpose. The points left open are a
 * liability cap, a contractual penalty, an availability the company answers
 * for and the day the terms take effect: a plausible number in their place
 * would read as one somebody agreed to.
 *
 * The terms were published with these points still open, on the owners'
 * decision (27 Oct 2026). `vopSet.test.ts` lists every one by name, so a
 * point is closed by deleting it from the text and from that list, and none
 * can be opened without the list saying so. LegalPage marks each in the text.
 */

import type { LegalClause, LegalDoc } from "../legalContent";

type Language = "cs" | "en";

/** One marker. A fresh expression for each use: a global one keeps its place between calls. */
const openPointPattern = (): RegExp => /\[ (?:DOPLNIT|TO FILL) — [^\]]+ \]/g;

/** The marker for a point left open, in the document's language. */
export function openPoint(lang: Language, what: string): string {
    return lang === "cs" ? `[ DOPLNIT — ${what} ]` : `[ TO FILL — ${what} ]`;
}

export interface OpenPointPart {
    text: string;
    open: boolean;
}

/** A text cut into what is settled and what is still open, in reading order. */
export function splitOpenPoints(text: string): OpenPointPart[] {
    const parts: OpenPointPart[] = [];
    let from = 0;
    for (const match of text.matchAll(openPointPattern())) {
        const at = match.index ?? 0;
        if (at > from) parts.push({ text: text.slice(from, at), open: false });
        parts.push({ text: match[0], open: true });
        from = at + match[0].length;
    }
    if (from < text.length || parts.length === 0) parts.push({ text: text.slice(from), open: false });
    return parts;
}

const itemText = (item: NonNullable<LegalClause["items"]>[number]): string =>
    typeof item === "string" ? item : `${item.term} ${item.text}`;

/** Every open point of a document, in the order a reader meets them. */
export function legalOpenPoints(doc: LegalDoc): string[] {
    const texts: string[] = [doc.title, doc.effectiveDate, ...(doc.intro ?? [])];
    for (const section of doc.sections) {
        texts.push(section.heading);
        for (const entry of section.definitions ?? []) texts.push(entry.term, entry.value);
        texts.push(...(section.paragraphs ?? []), ...(section.bullets ?? []));
        for (const clause of section.clauses ?? []) {
            texts.push(clause.text, ...(clause.items ?? []).map(itemText));
            if (clause.closing) texts.push(clause.closing);
        }
        texts.push(...(section.notes ?? []));
    }
    return texts.flatMap((text) => [...text.matchAll(openPointPattern())].map((match) => match[0]));
}
